import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface YearContextValue {
  selectedYear: number;
  setSelectedYear: (year: number) => void;
  availableYears: number[];
}

const STORAGE_KEY = "selected-year";
const DEFAULT_YEAR = new Date().getFullYear();

const YearContext = createContext<YearContextValue | undefined>(undefined);

export const YearProvider = ({ children }: { children: ReactNode }) => {
  const [selectedYear, setSelectedYearState] = useState<number>(() => {
    if (typeof window === "undefined") return DEFAULT_YEAR;
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? parseInt(stored, 10) : DEFAULT_YEAR;
  });

  const { data: availableYears = [DEFAULT_YEAR] } = useQuery({
    queryKey: ["available-years"],
    queryFn: async () => {
      const { data } = await supabase
        .from("batches")
        .select("year")
        .not("year", "is", null);
      const years = Array.from(new Set((data || []).map((b: any) => b.year as number))).sort((a, b) => b - a);
      if (!years.includes(DEFAULT_YEAR)) years.unshift(DEFAULT_YEAR);
      return years;
    },
    staleTime: 5 * 60 * 1000,
  });

  const setSelectedYear = (year: number) => {
    setSelectedYearState(year);
    if (typeof window !== "undefined") localStorage.setItem(STORAGE_KEY, String(year));
  };

  useEffect(() => {
    // If selected year not in list (e.g. first load), pick the most recent available
    if (availableYears.length && !availableYears.includes(selectedYear)) {
      setSelectedYear(availableYears[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableYears]);

  return (
    <YearContext.Provider value={{ selectedYear, setSelectedYear, availableYears }}>
      {children}
    </YearContext.Provider>
  );
};

export const useYear = () => {
  const ctx = useContext(YearContext);
  if (!ctx) throw new Error("useYear must be used inside YearProvider");
  return ctx;
};